import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from './use-auth'
import { authorizedFetch, parseApiErrorMessage } from '@/lib/api'
import { PROFILE_QUERY_ROOT, profileQueryKey } from '@/lib/user-session-cache'

export { PROFILE_AVATAR_MAX_BYTES, PROFILE_BANNER_MAX_BYTES } from '@/lib/profile-avatar-media'

async function profileRequestErrorMessage(response: Response): Promise<string> {
  return parseApiErrorMessage(response, `Request failed (${response.status})`)
}

export interface ProfileData {
  id?: string
  full_name?: string
  email?: string
  /** Current or targeted professional role (single line). */
  headline?: string
  bio?: string
  city?: string
  country?: string
  phone?: string
  website?: string
  linkedin_url?: string
  x_url?: string
  instagram_url?: string
  facebook_url?: string
  threads_url?: string
  tiktok_url?: string
  youtube_url?: string
  substack_url?: string
  github_url?: string
  extra_social_order?: string[]
  first_name?: string
  last_name?: string
  /** Account-owned full name (resolved on linked profiles). */
  name?: string | null
  /** Account-owned preferred name for in-app greetings. */
  greeting_name?: string | null
  /** Absolute URL to uploaded profile photo when set. */
  avatar_url?: string | null
  /** Absolute URL to uploaded profile cover banner when set. */
  banner_url?: string | null
  /** Public share slug for `/p/{slug}` when public profiles are enabled. */
  public_slug?: string | null
  /** Domain sections keyed by extension name; employment adds `candidate`. */
  extensions?: ProfileExtensions
}

export interface ProfileExtensions {
  candidate?: CandidateProfileSections
  [name: string]: object | undefined
}

/** `extensions.candidate`: work history and skills from the employment domain. */
export interface CandidateProfileSections {
  open_to_work?: boolean
  skills?: string[]
  experiences?: Experience[]
  papers?: ProfilePaper[]
  education?: Education[]
  certifications?: Certification[]
  skill_summaries?: SkillSummary[]
}

export function candidateSections(
  profile: ProfileData | null | undefined,
): CandidateProfileSections {
  return profile?.extensions?.candidate ?? {}
}

export function withCandidateSections(
  profile: ProfileData,
  update: (sections: CandidateProfileSections) => CandidateProfileSections,
): ProfileData {
  return {
    ...profile,
    extensions: { ...profile.extensions, candidate: update(candidateSections(profile)) },
  }
}

/** Profile update payload writing only `extensions.candidate` keys. */
export function candidateUpdate(sections: CandidateProfileSections): Partial<ProfileData> {
  return { extensions: { candidate: sections } }
}

export interface Experience {
  id: string
  title: string
  company: string
  description?: string
  start_date: string
  end_date?: string | null
  skills?: string[]
}

export interface Education {
  id?: string
  institution: string
  degree: string
  field_of_study?: string
  start_date?: string | null
  end_date?: string | null
  description?: string
  related_skills?: string[]
}

export interface Certification {
  id?: string
  name: string
  issuer: string
  issued_at?: string | null
  expires_at?: string | null
  credential_id?: string
  credential_url?: string
  description?: string
  related_skills?: string[]
}

export interface ProfilePaper {
  id?: string
  title: string
  authors?: string
  venue?: string
  year?: number | null
  url?: string
  doi?: string
  description?: string
}

export interface SkillSummary {
  skill_id: string
  skill_name: string
  proficiency_score: number
  proficiency_label: string
  experience_months: number
  is_current: boolean
  last_used_at?: string | null
  recency_label: string
  evidence_counts: {
    experiences: number
    education: number
    certifications: number
  }
}

/**
 * Dev-only profile simulations via sessionStorage (then reload `/profile`).
 * Instructions: docs/UI_PROFILE_DEV_SIMULATIONS.md
 */
const SIMULATE_PROFILE_FETCH_ERROR_KEY = 'ui:simulateProfileError'
/** `setItem(..., '1')` — failed fetch / error panel. Remove key to restore. */
const SIMULATE_EMPTY_PROFILE_KEY = 'ui:simulateEmptyProfile'
/** `setItem(..., '1')` — successful response with empty sections (name, email, skills, etc.). Do not set together with the error key (error wins). */

const DEV_EMPTY_PROFILE_FIXTURE: ProfileData = {
  id: '00000000-0000-4000-8000-000000000001',
  full_name: '',
  email: '',
  first_name: '',
  last_name: '',
  headline: undefined,
  bio: undefined,
  city: undefined,
  country: undefined,
  avatar_url: null,
  banner_url: null,
  extensions: {
    candidate: {
      skills: [],
      experiences: [],
      papers: [],
      education: [],
      certifications: [],
      skill_summaries: [],
    },
  },
}

function shouldSimulateProfileFetchError(): boolean {
  if (!import.meta.env.DEV || typeof sessionStorage === 'undefined') return false
  try {
    return sessionStorage.getItem(SIMULATE_PROFILE_FETCH_ERROR_KEY) === '1'
  } catch {
    return false
  }
}

function shouldSimulateEmptyProfile(): boolean {
  if (!import.meta.env.DEV || typeof sessionStorage === 'undefined') return false
  try {
    return sessionStorage.getItem(SIMULATE_EMPTY_PROFILE_KEY) === '1'
  } catch {
    return false
  }
}

export function useProfile() {
  const { token, user } = useAuth()
  const queryClient = useQueryClient()
  const profileKey = profileQueryKey(user?.id)

  const invalidateProfile = () => {
    void queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_ROOT })
  }

  // Query for fetching profile data
  const profileQuery = useQuery({
    queryKey: profileKey,
    queryFn: async (): Promise<ProfileData> => {
      if (!token) throw new Error('Not authenticated')

      if (shouldSimulateProfileFetchError()) {
        throw new Error('Failed to fetch profile')
      }

      if (shouldSimulateEmptyProfile()) {
        return { ...DEV_EMPTY_PROFILE_FIXTURE }
      }

      const response = await authorizedFetch('/api/profiles/my')

      if (!response.ok) {
        throw new Error('Failed to fetch profile')
      }

      return response.json()
    },
    enabled: !!token,
  })

  // Mutation for updating profile data
  const updateProfileMutation = useMutation({
    mutationFn: async (data: Partial<ProfileData>): Promise<ProfileData> => {
      if (!token) throw new Error('Not authenticated')
      const payload = { ...data }
      if (payload.extensions?.candidate?.skill_summaries) {
        const candidate = { ...payload.extensions.candidate }
        delete candidate.skill_summaries
        payload.extensions = { ...payload.extensions, candidate }
      }

      const response = await authorizedFetch('/api/profiles/my', {
        method: 'POST',
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to update profile')
      }

      return response.json()
    },
    onSuccess: (data) => {
      // Update the cache with the new data
      queryClient.setQueryData(profileKey, data)
    },
  })

  const uploadBannerMutation = useMutation({
    mutationFn: async (file: File): Promise<ProfileData> => {
      if (!token) throw new Error('Not authenticated')
      const body = new FormData()
      body.append('file', file)
      const response = await authorizedFetch('/api/profiles/my/banner', {
        method: 'POST',
        body,
      })
      if (!response.ok) {
        throw new Error(await profileRequestErrorMessage(response))
      }
      return response.json()
    },
    onSuccess: (data) => {
      queryClient.setQueryData(profileKey, data)
    },
  })

  const deleteBannerMutation = useMutation({
    mutationFn: async (): Promise<ProfileData> => {
      if (!token) throw new Error('Not authenticated')
      const response = await authorizedFetch('/api/profiles/my/banner', {
        method: 'DELETE',
      })
      if (!response.ok) {
        throw new Error(await profileRequestErrorMessage(response))
      }
      return response.json()
    },
    onSuccess: (data) => {
      queryClient.setQueryData(profileKey, data)
    },
  })

  // Mutation: add experience
  const addExperienceMutation = useMutation({
    mutationFn: async (exp: Omit<Experience, 'id'>): Promise<Experience> => {
      if (!token) throw new Error('Not authenticated')

      const response = await authorizedFetch('/api/profiles/my/experiences', {
        method: 'POST',
        body: JSON.stringify(exp),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to add experience')
      }

      return response.json()
    },
    onSuccess: (newExp) => {
      // Update cached profile
      queryClient.setQueryData(profileKey, (oldData: ProfileData | undefined) => {
        if (!oldData) return oldData
        return withCandidateSections(oldData, (c) => ({
          ...c,
          experiences: [newExp, ...(c.experiences ?? [])],
        }))
      })
    },
  })

  // Mutation: update experience
  const updateExperienceMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string, updates: Partial<Omit<Experience, 'id'>> }): Promise<Experience> => {
      if (!token) throw new Error('Not authenticated')

      const response = await authorizedFetch(`/api/profiles/my/experiences/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(updates),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to update experience')
      }

      return response.json()
    },
    onSuccess: (updatedExp) => {
      queryClient.setQueryData(profileKey, (oldData: ProfileData | undefined) => {
        if (!oldData) return oldData
        return withCandidateSections(oldData, (c) => ({
          ...c,
          experiences: c.experiences?.map((e) => (e.id === updatedExp.id ? updatedExp : e)),
        }))
      })
    },
  })

  // Mutation: delete experience
  const deleteExperienceMutation = useMutation({
    mutationFn: async (id: string): Promise<string> => {
      if (!token) throw new Error('Not authenticated')

      const response = await authorizedFetch(`/api/profiles/my/experiences/${id}`, {
        method: 'DELETE',
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to delete experience')
      }

      return id
    },
    onSuccess: (deletedId) => {
      queryClient.setQueryData(profileKey, (oldData: ProfileData | undefined) => {
        if (!oldData) return oldData
        return withCandidateSections(oldData, (c) => ({
          ...c,
          experiences: c.experiences?.filter((e) => e.id !== deletedId),
        }))
      })
      invalidateProfile()
    },
  })

  // Mutation: add skills to experience (batched)
  const addExperienceSkillsMutation = useMutation({
    mutationFn: async ({ experienceId, skillNames }: { experienceId: string, skillNames: string[] }): Promise<Experience> => {
      if (!token) throw new Error('Not authenticated')

      const response = await authorizedFetch(`/api/profiles/my/experiences/${experienceId}/skills`, {
        method: 'POST',
        body: JSON.stringify({ skill_names: skillNames }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to add skills to experience')
      }

      return response.json()
    },
    onSuccess: (updatedExp) => {
      queryClient.setQueryData(profileKey, (oldData: ProfileData | undefined) => {
        if (!oldData) return oldData
        return withCandidateSections(oldData, (c) => ({
          ...c,
          experiences: c.experiences?.map((e) => (e.id === updatedExp.id ? updatedExp : e)),
        }))
      })
      invalidateProfile()
    },
  })

  // Mutation: add education
  const addEducationMutation = useMutation({
    mutationFn: async (edu: Omit<Education, 'id'>): Promise<Education> => {
      if (!token) throw new Error('Not authenticated')

      const payload = {
        institution: edu.institution,
        degree: edu.degree,
        field_of_study: edu.field_of_study,
        start_date: edu.start_date,
        end_date: edu.end_date,
        description: edu.description,
        related_skill_names: edu.related_skills ?? [],
      }

      const response = await authorizedFetch('/api/profiles/my/education', {
        method: 'POST',
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to add education')
      }

      return response.json()
    },
    onSuccess: (newEdu) => {
      queryClient.setQueryData(profileKey, (oldData: ProfileData | undefined) => {
        if (!oldData) return oldData
        return withCandidateSections(oldData, (c) => ({
          ...c,
          education: [newEdu, ...(c.education ?? [])],
        }))
      })
      invalidateProfile()
    },
  })

  // Mutation: update education
  const updateEducationMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string, updates: Partial<Omit<Education, 'id'>> }): Promise<Education> => {
      if (!token) throw new Error('Not authenticated')

      const payload: Record<string, unknown> = {}
      if (updates.institution !== undefined) payload.institution = updates.institution
      if (updates.degree !== undefined) payload.degree = updates.degree
      if (updates.field_of_study !== undefined) payload.field_of_study = updates.field_of_study
      if (updates.start_date !== undefined) payload.start_date = updates.start_date
      if (updates.end_date !== undefined) payload.end_date = updates.end_date
      if (updates.description !== undefined) payload.description = updates.description
      if (updates.related_skills !== undefined) payload.related_skill_names = updates.related_skills

      const response = await authorizedFetch(`/api/profiles/my/education/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to update education')
      }

      return response.json()
    },
    onSuccess: (updatedEdu) => {
      queryClient.setQueryData(profileKey, (oldData: ProfileData | undefined) => {
        if (!oldData) return oldData
        return withCandidateSections(oldData, (c) => ({
          ...c,
          education: c.education?.map((e) => (e.id === updatedEdu.id ? updatedEdu : e)),
        }))
      })
      invalidateProfile()
    },
  })

  // Mutation: delete education
  const deleteEducationMutation = useMutation({
    mutationFn: async (id: string): Promise<string> => {
      if (!token) throw new Error('Not authenticated')

      const response = await authorizedFetch(`/api/profiles/my/education/${id}`, {
        method: 'DELETE',
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to delete education')
      }

      return id
    },
    onSuccess: (deletedId) => {
      queryClient.setQueryData(profileKey, (oldData: ProfileData | undefined) => {
        if (!oldData) return oldData
        return withCandidateSections(oldData, (c) => ({
          ...c,
          education: c.education?.filter((e) => e.id !== deletedId),
        }))
      })
      invalidateProfile()
    },
  })

  // Mutation: add certification
  const addCertificationMutation = useMutation({
    mutationFn: async (cert: Omit<Certification, 'id'>): Promise<Certification> => {
      if (!token) throw new Error('Not authenticated')

      const payload = {
        name: cert.name,
        issuer: cert.issuer,
        issued_at: cert.issued_at,
        expires_at: cert.expires_at,
        credential_id: cert.credential_id,
        credential_url: cert.credential_url,
        description: cert.description,
        related_skill_names: cert.related_skills ?? [],
      }

      const response = await authorizedFetch('/api/profiles/my/certifications', {
        method: 'POST',
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to add certification')
      }

      return response.json()
    },
    onSuccess: (newCert) => {
      queryClient.setQueryData(profileKey, (oldData: ProfileData | undefined) => {
        if (!oldData) return oldData
        return withCandidateSections(oldData, (c) => ({
          ...c,
          certifications: [newCert, ...(c.certifications ?? [])],
        }))
      })
      invalidateProfile()
    },
  })

  // Mutation: update certification
  const updateCertificationMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string, updates: Partial<Omit<Certification, 'id'>> }): Promise<Certification> => {
      if (!token) throw new Error('Not authenticated')

      const payload: Record<string, unknown> = {}
      if (updates.name !== undefined) payload.name = updates.name
      if (updates.issuer !== undefined) payload.issuer = updates.issuer
      if (updates.issued_at !== undefined) payload.issued_at = updates.issued_at
      if (updates.expires_at !== undefined) payload.expires_at = updates.expires_at
      if (updates.credential_id !== undefined) payload.credential_id = updates.credential_id
      if (updates.credential_url !== undefined) payload.credential_url = updates.credential_url
      if (updates.description !== undefined) payload.description = updates.description
      if (updates.related_skills !== undefined) payload.related_skill_names = updates.related_skills

      const response = await authorizedFetch(`/api/profiles/my/certifications/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to update certification')
      }

      return response.json()
    },
    onSuccess: (updatedCert) => {
      queryClient.setQueryData(profileKey, (oldData: ProfileData | undefined) => {
        if (!oldData) return oldData
        return withCandidateSections(oldData, (c) => ({
          ...c,
          certifications: c.certifications?.map((row) =>
            row.id === updatedCert.id ? updatedCert : row,
          ),
        }))
      })
      invalidateProfile()
    },
  })

  // Mutation: delete certification
  const deleteCertificationMutation = useMutation({
    mutationFn: async (id: string): Promise<string> => {
      if (!token) throw new Error('Not authenticated')

      const response = await authorizedFetch(`/api/profiles/my/certifications/${id}`, {
        method: 'DELETE',
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to delete certification')
      }

      return id
    },
    onSuccess: (deletedId) => {
      queryClient.setQueryData(profileKey, (oldData: ProfileData | undefined) => {
        if (!oldData) return oldData
        return withCandidateSections(oldData, (c) => ({
          ...c,
          certifications: c.certifications?.filter((row) => row.id !== deletedId),
        }))
      })
      invalidateProfile()
    },
  })

  // Mutation: add paper
  const addPaperMutation = useMutation({
    mutationFn: async (paper: Omit<ProfilePaper, 'id'>): Promise<ProfilePaper> => {
      if (!token) throw new Error('Not authenticated')

      const payload = {
        title: paper.title,
        authors: paper.authors,
        venue: paper.venue,
        year: paper.year ?? null,
        url: paper.url,
        doi: paper.doi,
        description: paper.description,
      }

      const response = await authorizedFetch('/api/profiles/my/papers', {
        method: 'POST',
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to add publication')
      }

      return response.json()
    },
    onSuccess: (newPaper) => {
      queryClient.setQueryData(profileKey, (oldData: ProfileData | undefined) => {
        if (!oldData) return oldData
        return withCandidateSections(oldData, (c) => ({
          ...c,
          papers: [newPaper, ...(c.papers ?? [])],
        }))
      })
      invalidateProfile()
    },
  })

  // Mutation: update paper
  const updatePaperMutation = useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string
      updates: Partial<Omit<ProfilePaper, 'id'>>
    }): Promise<ProfilePaper> => {
      if (!token) throw new Error('Not authenticated')

      const payload: Record<string, unknown> = {}
      if (updates.title !== undefined) payload.title = updates.title
      if (updates.authors !== undefined) payload.authors = updates.authors
      if (updates.venue !== undefined) payload.venue = updates.venue
      if (updates.year !== undefined) payload.year = updates.year
      if (updates.url !== undefined) payload.url = updates.url
      if (updates.doi !== undefined) payload.doi = updates.doi
      if (updates.description !== undefined) payload.description = updates.description

      const response = await authorizedFetch(`/api/profiles/my/papers/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to update publication')
      }

      return response.json()
    },
    onSuccess: (updatedPaper) => {
      queryClient.setQueryData(profileKey, (oldData: ProfileData | undefined) => {
        if (!oldData) return oldData
        return withCandidateSections(oldData, (c) => ({
          ...c,
          papers: c.papers?.map((row) => (row.id === updatedPaper.id ? updatedPaper : row)),
        }))
      })
      invalidateProfile()
    },
  })

  // Mutation: delete paper
  const deletePaperMutation = useMutation({
    mutationFn: async (id: string): Promise<string> => {
      if (!token) throw new Error('Not authenticated')

      const response = await authorizedFetch(`/api/profiles/my/papers/${id}`, {
        method: 'DELETE',
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to delete publication')
      }

      return id
    },
    onSuccess: (deletedId) => {
      queryClient.setQueryData(profileKey, (oldData: ProfileData | undefined) => {
        if (!oldData) return oldData
        return withCandidateSections(oldData, (c) => ({
          ...c,
          papers: c.papers?.filter((row) => row.id !== deletedId),
        }))
      })
      invalidateProfile()
    },
  })

  // Mutation: remove skill(s) from experience (batched)
  const removeExperienceSkillsMutation = useMutation({
    mutationFn: async ({
      experienceId,
      skillNames,
    }: {
      experienceId: string
      skillNames: string[]
    }): Promise<Experience> => {
      if (!token) throw new Error('Not authenticated')

      const response = await authorizedFetch(`/api/profiles/my/experiences/${experienceId}/skills`, {
        method: 'DELETE',
        body: JSON.stringify({ skill_names: skillNames }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({})) as Record<string, unknown>
        const detail = errorData.detail
        const detailStr =
          typeof detail === 'string'
            ? detail
            : Array.isArray(detail) && typeof detail[0]?.msg === 'string'
              ? detail[0].msg
              : undefined
        const message =
          (typeof errorData.error === 'string' && errorData.error) ||
          detailStr ||
          (typeof errorData.message === 'string' && errorData.message)
        throw new Error(message || `Failed to remove skills from experience (${response.status})`)
      }

      return response.json()
    },
    onSuccess: (updatedExp) => {
      queryClient.setQueryData(profileKey, (oldData: ProfileData | undefined) => {
        if (!oldData) return oldData
        return withCandidateSections(oldData, (c) => ({
          ...c,
          experiences: c.experiences?.map((e) => (e.id === updatedExp.id ? updatedExp : e)),
        }))
      })
      invalidateProfile()
    },
  })

  return {
    profile: profileQuery.data,
    isLoading: profileQuery.isLoading,
    isFetching: profileQuery.isFetching,
    isError: profileQuery.isError,
    error: profileQuery.error,
    refetch: profileQuery.refetch,
    updateProfile: updateProfileMutation.mutate,
    isUpdating: updateProfileMutation.isPending,
    updateError: updateProfileMutation.error,
    uploadBanner: uploadBannerMutation.mutateAsync,
    isUploadingBanner: uploadBannerMutation.isPending,
    uploadBannerError: uploadBannerMutation.error,
    deleteBanner: deleteBannerMutation.mutateAsync,
    isDeletingBanner: deleteBannerMutation.isPending,
    deleteBannerError: deleteBannerMutation.error,
    addExperience: addExperienceMutation.mutate,
    addExperienceAsync: addExperienceMutation.mutateAsync,
    isAddingExperience: addExperienceMutation.isPending,
    addExperienceError: addExperienceMutation.error,
    updateExperience: updateExperienceMutation.mutate,
    updateExperienceAsync: updateExperienceMutation.mutateAsync,
    isUpdatingExperience: updateExperienceMutation.isPending,
    updateExperienceError: updateExperienceMutation.error,
    deleteExperience: deleteExperienceMutation.mutate,
    deleteExperienceAsync: deleteExperienceMutation.mutateAsync,
    isDeletingExperience: deleteExperienceMutation.isPending,
    deleteExperienceError: deleteExperienceMutation.error,
    addExperienceSkillsAsync: addExperienceSkillsMutation.mutateAsync,
    removeExperienceSkillsAsync: removeExperienceSkillsMutation.mutateAsync,
    addEducationAsync: addEducationMutation.mutateAsync,
    isAddingEducation: addEducationMutation.isPending,
    addEducationError: addEducationMutation.error,
    updateEducationAsync: updateEducationMutation.mutateAsync,
    isUpdatingEducation: updateEducationMutation.isPending,
    updateEducationError: updateEducationMutation.error,
    deleteEducationAsync: deleteEducationMutation.mutateAsync,
    isDeletingEducation: deleteEducationMutation.isPending,
    deleteEducationError: deleteEducationMutation.error,
    addCertificationAsync: addCertificationMutation.mutateAsync,
    isAddingCertification: addCertificationMutation.isPending,
    addCertificationError: addCertificationMutation.error,
    updateCertificationAsync: updateCertificationMutation.mutateAsync,
    isUpdatingCertification: updateCertificationMutation.isPending,
    updateCertificationError: updateCertificationMutation.error,
    deleteCertificationAsync: deleteCertificationMutation.mutateAsync,
    isDeletingCertification: deleteCertificationMutation.isPending,
    deleteCertificationError: deleteCertificationMutation.error,
    addPaperAsync: addPaperMutation.mutateAsync,
    isAddingPaper: addPaperMutation.isPending,
    addPaperError: addPaperMutation.error,
    updatePaperAsync: updatePaperMutation.mutateAsync,
    isUpdatingPaper: updatePaperMutation.isPending,
    updatePaperError: updatePaperMutation.error,
    deletePaperAsync: deletePaperMutation.mutateAsync,
    isDeletingPaper: deletePaperMutation.isPending,
    deletePaperError: deletePaperMutation.error,
  }
}
